import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Clock } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Badge } from './ui/badge';

const Footer = () => {
  const { t, language } = useLanguage();
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-[#0b130e] text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <div className="w-10 h-10 bg-gradient-to-br from-[#1e7d55] to-[#1a5c3b] rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-lg">CA</span>
              </div>
              <div className="flex flex-col">
                <span className="text-lg font-bold">Credit Auto Reset</span>
              </div>
            </div>
            <p className="text-gray-400 text-sm leading-relaxed">
              {t.footer.tagline}
            </p>
            <div className="flex flex-wrap gap-2">
              <Badge variant="outline" className="bg-green-900/30 border-[#1a5c3b] text-green-400">
                {t.footer.badge1}
              </Badge>
              <Badge variant="outline" className="bg-green-900/30 border-[#1a5c3b] text-green-400">
                {t.footer.badge2}
              </Badge>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-semibold mb-4 !text-white">{t.footer.linksTitle}</h3>
            <ul className="space-y-3">
              <li>
                <Link to="/" className="text-gray-400 hover:text-green-400 transition-colors text-sm">
                  {t.footer.home}
                </Link>
              </li>
              <li>
                <Link to="/credit-analysis" className="text-gray-400 hover:text-green-400 transition-colors text-sm">
                  {t.footer.creditAnalysis}
                </Link>
              </li>
              <li>
                <Link to="/demander-un-pret-auto" className="text-gray-400 hover:text-green-400 transition-colors text-sm">
                  {t.footer.loanRequest}
                </Link>
              </li>
              <li>
                <Link to="/notre-equipe" className="text-gray-400 hover:text-green-400 transition-colors text-sm">
                  {t.footer.ourTeam}
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-gray-400 hover:text-green-400 transition-colors text-sm">
                  {t.footer.contact}
                </Link>
              </li>
            </ul>
            <h3 className="text-lg font-semibold mt-8 mb-4 !text-white">{language === 'en' ? 'Resources' : 'Ressources'}</h3>
            <ul className="space-y-3" data-testid="footer-resources">
              {[
                ['/qui-sommes-nous', 'Qui sommes-nous ?', 'About us'],
                ['/verifier-admissibilite', 'Préqualification rapide', 'Quick pre-qualification'],
                ['/calculateur-pret-auto', 'Calculateur de prêt auto', 'Car loan calculator'],
                ['/cote-de-credit-pret-auto', 'Cote de crédit et prêt auto', 'Credit score & car loans'],
                ['/retablir-son-credit', 'Rétablir son crédit', 'Rebuild your credit'],
              ].map(([to, fr, en]) => (
                <li key={to}>
                  <Link to={to} className="text-gray-400 hover:text-green-400 transition-colors text-sm" data-testid={`footer-link-${to.slice(1)}`}>
                    {language === 'en' ? en : fr}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-lg font-semibold mb-4 !text-white">{t.footer.contactTitle}</h3>
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <MapPin className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="text-gray-300">Credit Auto Reset</p>
                  <p className="text-gray-400">{t.footer.address}</p>
                  <p className="text-gray-400">{t.footer.city}</p>
                  <p className="text-gray-400">{t.footer.postal}</p>
                  <a
                    href="https://maps.google.com/?q=1007+Boulevard+René-Lévesque,+Verdun,+QC"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-green-400 hover:text-green-300 transition-colors text-xs mt-1 inline-block"
                  >
                    {t.footer.showMap} →
                  </a>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <Phone className="w-5 h-5 text-green-400 flex-shrink-0" />
                <div className="text-sm">
                  <p className="text-gray-400">{t.footer.sales}</p>
                  <a href="tel:514-418-3968" className="text-gray-300 hover:text-green-400 transition-colors font-semibold">
                    514-418-3968
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Hours */}
          <div>
            <h3 className="text-lg font-semibold mb-4 flex items-center space-x-2 !text-white">
              <Clock className="w-5 h-5 text-green-400" />
              <span>{t.footer.hoursTitle}</span>
            </h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between items-center py-1 border-b border-gray-700/50">
                <span className="text-gray-400">{t.footer.hours.monThu}</span>
                <span className="text-gray-300 font-medium">9:00 - 20:00</span>
              </div>
              <div className="flex justify-between items-center py-1 border-b border-gray-700/50">
                <span className="text-gray-400">{t.footer.hours.friday}</span>
                <span className="text-gray-300 font-medium">9:00 - 18:00</span>
              </div>
              <div className="flex justify-between items-center py-1 border-b border-gray-700/50">
                <span className="text-gray-400">{t.footer.hours.saturday}</span>
                <span className="text-gray-300 font-medium">10:00 - 16:00</span>
              </div>
              <div className="flex justify-between items-center py-1">
                <span className="text-gray-400">{t.footer.hours.sunday}</span>
                <span className="text-red-400 font-medium">{t.footer.hours.closed}</span>
              </div>
              <a 
                href="https://g.page/r/CXxH1z5HJ2FLEBM/review" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-2 mt-4 px-3 py-2 bg-green-900/50 rounded-lg hover:bg-green-800/50 transition-colors"
              >
                <svg className="w-4 h-4 text-yellow-400" fill="currentColor" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/></svg>
                <span className="text-green-300 text-xs font-medium">4.9/5 Google Reviews</span>
              </a>
            </div>
          </div>
        </div>

        {/* Partners Strip */}
        <div className="mt-8 pt-6 border-t border-gray-700/50">
          <p className="text-[10px] uppercase tracking-widest text-gray-400 text-center mb-4">
            {t.footer.linksTitle === 'Quick Links' ? 'Our Financial Partners' : 'Nos partenaires financiers'}
          </p>
          <div className="max-w-xl mx-auto bg-white rounded-lg px-6 py-4">
            <img 
              src="/images/partners.png" 
              alt="Desjardins, TD Bank, Scotiabank, iA, EdenPark, Santander, Iceberg Finance"
              className="w-full h-auto"
            />
          </div>
        </div>

        {/* Service Areas - Internal Links */}
        <div className="mt-10 pt-8 border-t border-gray-700">
          <h3 className="text-sm font-semibold text-gray-300 mb-3 text-center">{language === 'en' ? 'Service Areas' : 'Zones desservies'}</h3>
          <div className="flex flex-wrap justify-center gap-x-4 gap-y-1">
            {[
              { to: '/financement-auto-montreal', label: 'Montreal' },
              { to: '/financement-auto-laval', label: 'Laval' },
              { to: '/financement-auto-longueuil', label: 'Longueuil' },
              { to: '/financement-auto-brossard', label: 'Brossard' },
              { to: '/financement-auto-rive-sud', label: 'Rive-Sud' },
              { to: '/financement-auto-terrebonne', label: 'Terrebonne' },
              { to: '/financement-auto-repentigny', label: 'Repentigny' },
              { to: '/financement-auto-gatineau', label: 'Gatineau' },
              { to: '/financement-auto-quebec', label: 'Quebec' },
              { to: '/financement-auto-trois-rivieres', label: 'Trois-Rivieres' },
              { to: '/financement-auto-sherbrooke', label: 'Sherbrooke' },
              { to: '/financement-auto-drummondville', label: 'Drummondville' },
              { to: '/financement-auto-saint-jean', label: 'Saint-Jean' },
            ].map((city, i) => (
              <Link key={i} to={city.to} className="text-xs text-gray-400 hover:text-green-400 transition-colors">
                {city.label}
              </Link>
            ))}
            <Link to="/faq" className="text-xs text-gray-400 hover:text-green-400 transition-colors">FAQ</Link>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-8 pt-8 border-t border-gray-700">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-sm text-gray-400">
              © {currentYear} Credit Auto Reset — {t.footer.rights}
            </p>
            <Link to="/privacy" className="text-sm text-gray-400 hover:text-green-400 transition-colors">
              {t.footer.privacy}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;